import React, { useMemo, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { Plus, Edit3, Eye, RefreshCw, Trash2, Upload, MapPinned } from 'lucide-react';
import { backofficeApi, extractErrorMessage, formatNumber } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type {
  MerchantDto,
  MerchantRequestDto,
  MerchantStatus,
  MerchantPartnershipStatus,
  Tone,
  ComboItem,
} from '@kardume/types';
import {
  MERCHANT_STATUS_LABELS,
  EXTERNAL_LINK_ERROR_MESSAGES,
  BUSINESS_HOUR_ERROR_MESSAGES,
} from '@kardume/types';
import {
  PageHeader,
  StatCard,
  Table,
  TableContainer,
  Badge,
  Button,
  ActionButton,
  ConfirmModal,
  Avatar,
  SearchInput,
  Select,
  Pagination,
  formatDocument,
  formatPhone,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import { useDebouncedValue } from '../utils/useDebouncedValue';
import DataState from '../components/DataState';
import MerchantDetailDrawer from '../components/MerchantDetailDrawer';
import MerchantFormModal from '../components/MerchantFormModal';
import MerchantGuideModal from '../components/MerchantGuideModal';
import MerchantImportModal from '../components/MerchantImportModal';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: center;

  > *:first-child {
    flex: 1;
    min-width: 240px;
  }
`;

const Establishment = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  .info {
    min-width: 0;
  }
  strong {
    display: block;
    color: ${(p) => p.theme.textPrimary};
  }
  .rz {
    font-size: 0.8rem;
    color: ${(p) => p.theme.textMuted};
  }
  .doc {
    font-size: 0.8rem;
    color: ${(p) => p.theme.textSecondary};
  }
  .id {
    font-family: monospace;
    font-size: 0.68rem;
    color: ${(p) => p.theme.textMuted};
    text-transform: uppercase;
  }
`;

const Contact = styled.div`
  strong {
    display: block;
    font-weight: 600;
    color: ${(p) => p.theme.textPrimary};
  }
  span {
    display: block;
    font-size: 0.82rem;
    color: ${(p) => p.theme.textMuted};
  }
`;

const PlanCell = styled.div`
  strong {
    display: block;
    color: ${(p) => p.theme.textPrimary};
  }
  span {
    font-size: 0.8rem;
    color: ${(p) => p.theme.textMuted};
  }
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.4rem;
  justify-content: flex-end;
`;

const STATUS_OPTIONS: { value: MerchantStatus; label: string }[] = (
  Object.keys(MERCHANT_STATUS_LABELS) as MerchantStatus[]
).map((value) => ({ value, label: MERCHANT_STATUS_LABELS[value] }));

const FILTER_OPTIONS = [{ value: '', label: 'Todos os status' }, ...STATUS_OPTIONS];

/**
 * Filtro de parceria (card 226, regras §37.1).
 *
 * Separado do filtro de status de propósito: são eixos diferentes - "aparece no
 * catálogo?" e "existe parceria?" - e um seletor só impediria a pergunta que o
 * comercial de fato faz, que é "quais parceiras estão fora do ar".
 */
const PARTNERSHIP_LABELS: Record<MerchantPartnershipStatus, string> = {
  NONE: 'Sem parceria',
  PENDING: 'Em negociação',
  ACTIVE: 'Parceira',
  SUSPENDED: 'Suspensa',
  EXPIRED: 'Expirada',
  TERMINATED: 'Encerrada',
};

const PARTNERSHIP_FILTER_OPTIONS = [
  { value: '', label: 'Toda parceria' },
  ...(Object.keys(PARTNERSHIP_LABELS) as MerchantPartnershipStatus[]).map((value) => ({
    value,
    label: PARTNERSHIP_LABELS[value],
  })),
];

function statusTone(status: MerchantStatus): Tone {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'PENDING':
      return 'warning';
    case 'SUSPENDED':
      return 'danger';
    default:
      return 'secondary';
  }
}

const isBillingRisk = (m: MerchantDto): boolean =>
  m.status === 'SUSPENDED' || (!!m.billingStatus && /pend|atras|risc/i.test(m.billingStatus));

const MerchantsPage: React.FC = () => {
  const theme = useTheme();
  const { showNotification } = useNotification();

  // Estado de paginação/busca - a busca e o filtro de status são resolvidos no
  // servidor (GET /v1/backoffice/merchants aceita query, status e Pageable).
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [partnershipFilter, setPartnershipFilter] = useState('');

  const debouncedSearch = useDebouncedValue(search.trim(), 350);

  const { data, loading, error, reload } = useAsync(
    () =>
      backofficeApi.listMerchants({
        page,
        size: pageSize,
        query: debouncedSearch || undefined,
        status: statusFilter ? (statusFilter as MerchantStatus) : undefined,
        partnershipStatus: partnershipFilter
          ? (partnershipFilter as MerchantPartnershipStatus)
          : undefined,
      }),
    [page, pageSize, debouncedSearch, statusFilter, partnershipFilter]
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const semCoordenada = useAsync(() => backofficeApi.countMerchantsWithoutCoordinates(), []);
  const [editing, setEditing] = useState<MerchantDto | null>(null);
  const [saving, setSaving] = useState(false);
  const categoriesState = useAsync(() => backofficeApi.listCategories({ size: 200 }), []);
  const categoryItems = useMemo<ComboItem[]>(
    () => (categoriesState.data?.content ?? []).map((c) => ({ id: c.id, label: c.name })),
    [categoriesState.data]
  );
  const externalLinkPlatformsState = useAsync(() => backofficeApi.listExternalLinkPlatforms(), []);
  const localitiesState = useAsync(() => backofficeApi.listLocalities({ size: 200, active: true }), []);
  const localityItems = useMemo<ComboItem[]>(
    () => (localitiesState.data?.content ?? []).map((l) => ({ id: l.id, label: `${l.name}, ${l.state}` })),
    [localitiesState.data]
  );
  const [detail, setDetail] = useState<MerchantDto | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<MerchantDto | null>(null);
  const [deleting, setDeleting] = useState(false);

  /**
   * Exclusão do lojista. Suspender e excluir resolvem coisas diferentes:
   * suspender é reversível e mantém a loja na base; excluir desliga a assinatura,
   * tira os anúncios da vitrine e revoga o acesso da equipe ao painel.
   */
  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await backofficeApi.deleteMerchant(deleteTarget.id);
      showNotification('Lojista excluído.', 'success');
      setDeleteTarget(null);
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível excluir o lojista.'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const merchants = useMemo(() => data?.content ?? [], [data]);

  // "Total" respeita o filtro atual (totalElements do servidor). Os demais
  // cartões refletem a página carregada (não há endpoint de agregado global).
  const stats = useMemo(
    () => ({
      total: data?.totalElements ?? merchants.length,
      active: merchants.filter((m) => m.status === 'ACTIVE').length,
      // Card 226 - "parceiras" ao lado de "ativos", e não no lugar dele: a
      // distância entre os dois números é a informação nova que o guia da
      // cidade produziu, ou seja, quantas empresas o Kardume lista sem ter
      // relação comercial com elas.
      partners: merchants.filter((m) => m.partner).length,
      risk: merchants.filter(isBillingRisk).length,
    }),
    [data, merchants]
  );

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(0);
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setPage(0);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(0);
  };

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (m: MerchantDto) => {
    setEditing(m);
    setModalOpen(true);
  };

  const openDetail = (m: MerchantDto) => {
    setDetail(m);
    setDetailOpen(true);
  };

  const handleSave = async (payload: MerchantRequestDto) => {
    if (saving) return;
    setSaving(true);
    try {
      if (editing) {
        await backofficeApi.updateMerchant(editing.id, payload);
        showNotification('Lojista atualizado com sucesso.', 'success');
      } else {
        await backofficeApi.createMerchant(payload);
        showNotification('Lojista criado com sucesso.', 'success');
      }
      setModalOpen(false);
      await reload();
    } catch (err) {
      showNotification(
        extractErrorMessage(err, 'Não foi possível salvar o lojista.', {
          ...EXTERNAL_LINK_ERROR_MESSAGES,
          ...BUSINESS_HOUR_ERROR_MESSAGES,
        }),
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (m: MerchantDto) => {
    try {
      await backofficeApi.approveMerchant(m.id);
      showNotification('Lojista aprovado e ativado.', 'success');
      setDetailOpen(false);
      await reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível aprovar o lojista.'), 'error');
    }
  };

  const handleReject = async (m: MerchantDto, reason: string) => {
    try {
      await backofficeApi.rejectMerchant(m.id, reason);
      showNotification('Lojista rejeitado.', 'success');
      setDetailOpen(false);
      await reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível rejeitar o lojista.'), 'error');
    }
  };

  const totalPages = data?.totalPages ?? 0;
  const totalItems = data?.totalElements;

  return (
    <>
      <PageHeader
        title="Lojistas"
        subtitle="Gestão operacional, assinatura, cobrança e histórico dos lojistas."
        actions={
          <>
            {/* Card 231 - dois cadastros, e a diferença entre eles é o ponto:
                "Cadastrar no guia" leva um minuto e cria empresa sem parceria;
                "Novo lojista" é o cadastro comercial completo, com documento,
                responsável e checklist. Botões separados porque quem faz a
                curadoria da cidade e quem faz o onboarding comercial não são a
                mesma pessoa nem o mesmo momento. */}
            <Button variant="outline" onClick={() => setImportOpen(true)}>
              <Upload size={16} /> Importar
            </Button>
            <Button variant="secondary" onClick={() => setGuideOpen(true)}>
              <MapPinned size={16} /> Cadastrar no guia
            </Button>
            <Button variant="primary" onClick={openCreate}>
              <Plus size={16} /> Novo lojista
            </Button>
          </>
        }
      />

      <StatsGrid>
        <StatCard label="Total de lojistas" value={formatNumber(stats.total)} />
        <StatCard label="Ativos (nesta página)" value={formatNumber(stats.active)} />
        <StatCard label="Parceiras (nesta página)" value={formatNumber(stats.partners)} />
        <StatCard label="Com risco (nesta página)" value={formatNumber(stats.risk)} />
        {/*
          Card 150. Este é o número que diz se "mais próximas" é confiável: com
          metade da base sem coordenada, a vitrine por proximidade ordena metade
          do catálogo e empurra o resto para o fim. É a diferença entre a
          operação saber disso aqui e descobrir pela reclamação de um lojista.

          "Na base inteira", e não nesta página, de propósito - os outros três
          contam a página porque saem dela; este vem de uma consulta própria.
        */}
        <StatCard
          label="Sem coordenada (base inteira)"
          value={semCoordenada.loading ? '…' : formatNumber(semCoordenada.data ?? 0)}
        />
      </StatsGrid>

      <Toolbar>
        <SearchInput
          placeholder="Buscar por nome fantasia, razão social ou documento..."
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
        <Select
          options={FILTER_OPTIONS}
          value={statusFilter}
          onChange={(e) => handleStatusChange(e.target.value)}
        />
        <Select
          options={PARTNERSHIP_FILTER_OPTIONS}
          value={partnershipFilter}
          onChange={(e) => {
            setPartnershipFilter(e.target.value);
            setPage(0);
          }}
        />
        <ActionButton title="Atualizar" onClick={reload}>
          <RefreshCw size={16} />
        </ActionButton>
      </Toolbar>

      <DataState
        loading={loading}
        error={error}
        isEmpty={merchants.length === 0}
        emptyTitle="Nenhum lojista encontrado"
        emptyDescription="Ajuste os filtros ou cadastre um novo lojista."
        onRetry={reload}
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Estabelecimento</th>
                <th>Responsável</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Status</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Plano atual</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {merchants.map((m) => (
                <tr key={m.id}>
                  <td>
                    <Establishment>
                      <Avatar name={m.tradeName || m.name} src={m.logoUrl} />
                      <div className="info">
                        <strong>{m.tradeName || m.name}</strong>
                        {m.tradeName && m.name !== m.tradeName && <div className="rz">{m.name}</div>}
                        {m.document && <div className="doc">{formatDocument(m.document)}</div>}
                        <div className="id">ID: {m.id.slice(0, 8)}...</div>
                      </div>
                    </Establishment>
                  </td>
                  <td>
                    {/* Empresa do guia da cidade entra sem contato (card 241).
                        Um traço diz "não temos"; o espaço em branco que ficava
                        aqui parecia falha de carregamento. */}
                    <Contact>
                      <strong>{m.ownerName || '- sem responsável'}</strong>
                      <span>{m.email || '-'}</span>
                      <span>{m.phone ? formatPhone(m.phone) : '-'}</span>
                    </Contact>
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <Badge tone={statusTone(m.status)}>{MERCHANT_STATUS_LABELS[m.status]}</Badge>
                    {/* Só quando há parceria viva. Empresa apenas cadastrada
                        não recebe rótulo nenhum - nem aqui (§37.5). */}
                    {m.partner && (
                      <>
                        {' '}
                        <Badge tone="info">Parceira</Badge>
                      </>
                    )}
                    {/* Card 241: o cadastro deixou de exigir contato, equipe e
                        documento, então empresa incompleta virou o caso comum e
                        não a exceção. O percentual aqui é o que substitui a
                        obrigatoriedade - antes o formulário recusava salvar,
                        agora a lista mostra o que falta terminar. Some quando o
                        onboarding fecha, para não virar ruído em toda linha. */}
                    {m.onboardingComplete === false && (
                      <div style={{ marginTop: '0.3rem' }}>
                        <Badge tone="warning">Onboarding {m.onboardingProgress ?? 0}%</Badge>
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <PlanCell>
                      <strong>{m.planName ?? '-'}</strong>
                      {m.billingStatus && <span>{m.billingStatus}</span>}
                    </PlanCell>
                  </td>
                  <td style={{ width: '1%', whiteSpace: 'nowrap' }}>
                    <RowActions>
                      <ActionButton title="Ver detalhes" onClick={() => openDetail(m)}>
                        <Eye size={16} />
                      </ActionButton>
                      <ActionButton title="Editar" onClick={() => openEdit(m)}>
                        <Edit3 size={16} />
                      </ActionButton>
                      <ActionButton title="Excluir" color={theme.error} onClick={() => setDeleteTarget(m)}>
                        <Trash2 size={16} />
                      </ActionButton>
                    </RowActions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>

      {!error && (
        <Pagination
          currentPage={page + 1}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          disabled={loading}
          onPageChange={(next) => setPage(next - 1)}
          onPageSizeChange={handlePageSizeChange}
        />
      )}

      <ConfirmModal
        isOpen={!!deleteTarget}
        danger
        title="Excluir lojista"
        message={
          <>
            Excluir <strong>{deleteTarget?.name}</strong>? A assinatura é cancelada, os anúncios saem
            do site e do app e a equipe perde o acesso ao painel - inclusive nas sessões abertas.
            Cupons já resgatados, cashback e faturas emitidas são preservados.
          </>
        }
        confirmLabel="Excluir"
        loading={deleting}
        onConfirm={confirmDelete}
        onClose={() => setDeleteTarget(null)}
      />

      <MerchantDetailDrawer
        merchant={detail}
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        onEdit={(m) => {
          setDetailOpen(false);
          openEdit(m);
        }}
        onApprove={handleApprove}
        onReject={handleReject}
      />

      <MerchantFormModal
        isOpen={modalOpen}
        merchant={editing}
        categories={categoryItems}
        categoriesLoading={categoriesState.loading}
        localities={localityItems}
        localitiesLoading={localitiesState.loading}
        externalLinkPlatforms={externalLinkPlatformsState.data ?? []}
        externalLinkPlatformsLoading={externalLinkPlatformsState.loading}
        saving={saving}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSave}
      />

      <MerchantGuideModal
        isOpen={guideOpen}
        categories={categoryItems}
        localities={localityItems}
        localitiesLoading={localitiesState.loading}
        onClose={() => setGuideOpen(false)}
        onCreated={() => reload()}
      />

      <MerchantImportModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onRegistered={() => reload()}
      />
    </>
  );
};

export default MerchantsPage;
